import { useBlockProps } from "@wordpress/block-editor";
import { useEffect, useRef, useState } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import apiFetch from "@wordpress/api-fetch";

const EMPTY_FORM_DATA = {
	people_email: "",
	people_title: "",
	people_city: "",
	location_name: "",
	street_address: "",
	postal_code: "",
	people_province: "",
	people_phone: "",
	linkedin: "",
	instagram: "",
	facebook: "",
	home: "",
	twitter: "",
	rank_number: "",
	rank_number_second: "",
};

const normalizeToString = (value) => (value == null ? "" : String(value));

const formatLocationString = ({ location_name, street_address, postal_code, people_city }) => {
	const parts = [];
	const ln = (location_name || "").trim();
	const sa = (street_address || "").trim();
	const pc = (postal_code || "").trim();
	const city = (people_city || "").trim();

	if (ln) parts.push(ln);
	if (sa) parts.push(sa);

	const last = `${pc} ${city}`.trim();
	if (last) parts.push(last);

	return parts.join(", ");
};

const EditUserForm = ({ postId, closeEditModal, sortedMembers, setSortedMembers }) => {
	const [formData, setFormData] = useState(EMPTY_FORM_DATA);

	// Prevent re-hydration from overwriting user typing.
	const didHydrateRef = useRef(false);
	const isDirtyRef = useRef(false);

	// Reset hydration/dirty flags when switching which person is being edited.
	useEffect(() => {
		didHydrateRef.current = false;
		isDirtyRef.current = false;
	}, [postId]);

	// Always fetch fresh data when opening the modal so the latest saved values show up
	// without reloading the editor. Cache-bust to avoid any intermediate HTTP caching.
	useEffect(() => {
		if (!postId) return;

		let isCancelled = false;

		apiFetch({ path: `/wp/v2/person/${postId}?context=edit&_=${Date.now()}` })
			.then((personRecord) => {
				if (isCancelled) return;
				if (isDirtyRef.current) return;

				const meta = personRecord && personRecord.person_meta_fields ? personRecord.person_meta_fields : null;
				if (meta) {
					setFormData({
						people_email: normalizeToString(meta.people_email),
						people_title: normalizeToString(meta.people_title),
						people_city: normalizeToString(meta.people_city),
						location_name: normalizeToString(meta.location_name),
						street_address: normalizeToString(meta.street_address),
						postal_code: normalizeToString(meta.postal_code),
						people_province: normalizeToString(meta.people_province),
						people_phone: normalizeToString(meta.people_phone),
						linkedin: normalizeToString(meta.linkedin),
						instagram: normalizeToString(meta.instagram),
						facebook: normalizeToString(meta.facebook),
						home: normalizeToString(meta.home),
						twitter: normalizeToString(meta.twitter),
						rank_number: normalizeToString(meta.rank_number),
						rank_number_second: normalizeToString(meta.rank_number_second),
					});
				}
			})
			.catch(() => {
				// Ignore and fall back to local sources below.
			});

		return () => {
			isCancelled = true;
		};
	}, [postId]);

	// Fast local hydration (from editor state / DOM) so the form isn't empty while the fresh fetch loads.
	// IMPORTANT: the modal can render once with a stale/empty postId due to batched state updates.
	useEffect(() => {
		if (!postId) return;
		if (didHydrateRef.current) return;
		if (isDirtyRef.current) return;

		// Prefer using already-loaded REST data in the editor preview.
		const person = Array.isArray(sortedMembers)
			? sortedMembers.find((p) => p && p.id === postId)
			: null;
		const meta = person && person.person_meta_fields ? person.person_meta_fields : null;

		if (meta) {
			setFormData({
				people_email: normalizeToString(meta.people_email),
				people_title: normalizeToString(meta.people_title),
				people_city: normalizeToString(meta.people_city),
				location_name: normalizeToString(meta.location_name),
				street_address: normalizeToString(meta.street_address),
				postal_code: normalizeToString(meta.postal_code),
				people_province: normalizeToString(meta.people_province),
				people_phone: normalizeToString(meta.people_phone),
				linkedin: normalizeToString(meta.linkedin),
				instagram: normalizeToString(meta.instagram),
				facebook: normalizeToString(meta.facebook),
				home: normalizeToString(meta.home),
				twitter: normalizeToString(meta.twitter),
				rank_number: normalizeToString(meta.rank_number),
				rank_number_second: normalizeToString(meta.rank_number_second),
			});
			didHydrateRef.current = true;
			return;
		}

		// Fallback: read from DOM (works if the preview is present)
		const $ = window.jQuery;
		if (typeof $ !== "function") return;

		const parentDiv = $(`.artcloud-peoples-item.${postId}`);
		setFormData({
			people_email: normalizeToString(parentDiv.find(".people_email").text()),
			people_title: normalizeToString(parentDiv.find(".people_title").text()),
			people_city: normalizeToString(parentDiv.find(".people_city").text()),
			location_name: "",
			street_address: "",
			postal_code: "",
			people_province: normalizeToString(parentDiv.find(".people_province").text()),
			people_phone: normalizeToString(parentDiv.find(".people_phone").text()),
			linkedin: normalizeToString(parentDiv.find("a.people_linkedin").attr("href")),
			instagram: normalizeToString(parentDiv.find("a.people_instagram").attr("href")),
			facebook: normalizeToString(parentDiv.find("a.people_facebook").attr("href")),
			home: normalizeToString(parentDiv.find("a.people_home").attr("href")),
			twitter: normalizeToString(parentDiv.find("a.people_twitter").attr("href")),
			rank_number: normalizeToString(parentDiv.find(".people_rank").first().text()),
			rank_number_second: normalizeToString(parentDiv.find(".people_rank.second").first().text()),
		});
		didHydrateRef.current = true;
	}, [postId, sortedMembers]);
	const handleChange = (event) => {
		const { name, value } = event.target;
		isDirtyRef.current = true;
		setFormData(prevFormData => ({
			...prevFormData,
			[name]: value
		}));
	};
	const savePeopleData = (event) => {
		event.preventDefault();
		const data = {
			post_id: postId,
			_people_email: formData.people_email,
			_people_title: formData.people_title,
			_people_city: formData.people_city,
			_people_province: formData.people_province,
			_location_name: formData.location_name,
			_street_address: formData.street_address,
			_postal_code: formData.postal_code,
			_people_phone: formData.people_phone,
			_linkedin_url: formData.linkedin,
			// Note: legacy meta key in this plugin is "_intagram_url" (without the "s")
			_intagram_url: formData.instagram,
			_facebook_url: formData.facebook,
			_homepage_url: formData.home,
			_twitter_url: formData.twitter,
			_rank_number: formData.rank_number,
			_rank_number_second: formData.rank_number_second,
		};

		// wp.ajax.post signature: wp.ajax.post( action, data )
		wp.ajax
			.post("update_person_data", { data })
			.done(function () {
				updateFormDataInDOM(postId, formData);

				// Also update the in-editor React state so preview stays in sync.
				if (typeof setSortedMembers === "function") {
					setSortedMembers((prev) =>
						Array.isArray(prev)
							? prev.map((p) =>
								p && p.id === postId
									? {
										...p,
										person_meta_fields: {
											...(p.person_meta_fields || {}),
											people_email: formData.people_email,
											people_title: formData.people_title,
											people_city: formData.people_city,
											people_province: formData.people_province,
											location_name: formData.location_name,
											street_address: formData.street_address,
											postal_code: formData.postal_code,
											people_phone: formData.people_phone,
											linkedin: formData.linkedin,
											instagram: formData.instagram,
											facebook: formData.facebook,
											home: formData.home,
											twitter: formData.twitter,
											rank_number: formData.rank_number,
											rank_number_second: formData.rank_number_second,
										},
									}
									: p
							)
							: prev
					);
				}

				closeEditModal();
			})
			.fail(function (err) {
				// eslint-disable-next-line no-console
				console.error("update_person_data failed", err);
			});
	};

	function updateFormDataInDOM(postId, formData) {
		const $ = window.jQuery;
		if (typeof $ !== "function") return;

		const parentDiv = $(`.artcloud-peoples-item.${postId}`);
		const locationString = formatLocationString(formData);
		const updateTextData = {
			'p.people_title': formData.people_title,
			'p.people_city': locationString,
			'p.people_province': formData.people_province,
			'p.people_phone': formData.people_phone,
			'p.people_email': formData.people_email,
			'h6.people_rank': formData.rank_number,
			'h6.people_rank.second': formData.rank_number_second,
		};

		const updateHrefData = {
			'a.people_linkedin': formData.linkedin,
			'a.people_instagram': formData.instagram,
			'a.people_facebook': formData.facebook,
			'a.people_home': formData.home,
			'a.people_twitter': formData.twitter
		};

		// Update text content
		for (const selector in updateTextData) {
			parentDiv.find(selector).text(updateTextData[selector]);
		}

		// Update href attribute
		for (const selector in updateHrefData) {
			parentDiv.find(selector).attr('href', updateHrefData[selector]);
		}
	}

	return (
		<div {...useBlockProps()}>
			<div className="artcloud-row">
				<form action="" method="post" onSubmit={savePeopleData}>
					<div className="col-12">
						<label htmlFor="people_email">{__("Email", "artcloud-manage-people")}</label>
						<input type="email" name="people_email" id="people_email" value={formData.people_email} onChange={handleChange} />
					</div>
					<div className="col-12">
						<label htmlFor="people_title">{__("Title", "artcloud-manage-people")}</label>
						<input type="text" name="people_title" id="people_title" value={formData.people_title} onChange={handleChange} />
					</div>
					<div className="col-12">
						<label htmlFor="location_name">{__("Location name", "artcloud-manage-people")}</label>
						<input type="text" name="location_name" id="location_name" value={formData.location_name} onChange={handleChange} />
					</div>
					<div className="col-12">
						<label htmlFor="street_address">{__("Street address", "artcloud-manage-people")}</label>
						<input type="text" name="street_address" id="street_address" value={formData.street_address} onChange={handleChange} />
					</div>
					<div className="col-12">
						<label htmlFor="people_city">{__("City", "artcloud-manage-people")}</label>
						<input type="text" name="people_city" id="people_city" value={formData.people_city} onChange={handleChange} />
					</div>

					<div className="col-12">
						<label htmlFor="postal_code">{__("Postal code", "artcloud-manage-people")}</label>
						<input type="text" name="postal_code" id="postal_code" value={formData.postal_code} onChange={handleChange} />
					</div>
					<div className="col-12">
						<label htmlFor="people_province">{__("Province", "artcloud-manage-people")}</label>
						<input type="text" name="people_province" id="people_province" value={formData.people_province} onChange={handleChange} />
					</div>
					<div className="col-12">
						<label htmlFor="people_phone">{__("Phone", "artcloud-manage-people")}</label>
						<input type="tel" name="people_phone" id="people_phone" value={formData.people_phone} onChange={handleChange} />
					</div>
					<div className="col-12">
						<label htmlFor="linkedin">{__("LinkedIn", "artcloud-manage-people")}</label>
						<input type="text" name="linkedin" id="linkedin" value={formData.linkedin} onChange={handleChange} />
					</div>
					<div className="col-12">
						<label htmlFor="instagram">{__("Instagram", "artcloud-manage-people")}</label>
						<input type="text" name="instagram" id="instagram" value={formData.instagram} onChange={handleChange} />
					</div>
					<div className="col-12">
						<label htmlFor="facebook">{__("Facebook", "artcloud-manage-people")}</label>
						<input type="text" name="facebook" id="facebook" value={formData.facebook} onChange={handleChange} />
					</div>
					<div className="col-12">
						<label htmlFor="home">{__("Homepage", "artcloud-manage-people")}</label>
						<input type="text" name="home" id="home" value={formData.home} onChange={handleChange} />
					</div>
					<div className="col-12">
						<label htmlFor="twitter">{__("Twitter", "artcloud-manage-people")}</label>
						<input type="text" name="twitter" id="twitter" value={formData.twitter} onChange={handleChange} />
					</div>
					<div className="col-12">
						<label htmlFor="rank_number">{__("Rank number", "artcloud-manage-people")}</label>
						<input type="number" name="rank_number" id="rank_number" value={formData.rank_number} onChange={handleChange} />
					</div>
					<div className="col-12">
						<label htmlFor="rank_number_second">{__("Secondary rank number", "artcloud-manage-people")}</label>
						<input type="number" name="rank_number_second" id="rank_number_second" value={formData.rank_number_second} onChange={handleChange} />
					</div>
					<button className="components-button is-primary" type="submit">{__("Save", "artcloud-manage-people")}</button>
				</form>
			</div>
		</div>
	);
}

export default EditUserForm;
